import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { seoMetadata } from '../data/seoMetadata';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  page?: string;
  calculator?: string;
  blogPost?: string;
}

const SEOHead: React.FC<SEOHeadProps> = ({ 
  title, 
  description, 
  keywords, 
  canonical,
  page = 'home',
  calculator,
  blogPost
}) => {
  const location = useLocation();

  // Self-referencing canonical: every page canonicalises to its OWN URL unless
  // an explicit canonical prop is passed. Previously a missing canonical fell
  // back to the home URL, so every calculator/blog page advertised
  // canonical="https://carloanpaymentcalculator.com.au/" and looked like a
  // duplicate of the homepage to Google.
  //
  // Normalise to a trailing slash: Netlify serves prerendered routes at
  // "/route/" and 301-redirects "/route" -> "/route/", so the canonical (and
  // og:url) must point at the trailing-slash form Google actually indexes,
  // never at the redirecting non-slash URL.
  const normalizedPath =
    location.pathname === '/' ? '/' : `${location.pathname.replace(/\/+$/, '')}/`;
  const metaCanonical =
    canonical || `https://carloanpaymentcalculator.com.au${normalizedPath}`;

  // If direct props are provided, use them; otherwise fall back to seoMetadata.
  let metaTitle = title;
  let metaDescription = description;
  let metaKeywords = keywords;

  if (!metaTitle || !metaDescription || !metaKeywords) {
    if (calculator && seoMetadata.calculators && seoMetadata.calculators[calculator as keyof typeof seoMetadata.calculators]) {
      const calcMeta = seoMetadata.calculators[calculator as keyof typeof seoMetadata.calculators];
      metaTitle = metaTitle || calcMeta.title;
      metaDescription = metaDescription || calcMeta.description;
      metaKeywords = metaKeywords || calcMeta.keywords;
    } else if (blogPost && seoMetadata.blogPost && seoMetadata.blogPost[blogPost as keyof typeof seoMetadata.blogPost]) {
      const blogMeta = seoMetadata.blogPost[blogPost as keyof typeof seoMetadata.blogPost];
      metaTitle = metaTitle || blogMeta.title;
      metaDescription = metaDescription || blogMeta.description;
      metaKeywords = metaKeywords || blogMeta.keywords;
    } else if (page && seoMetadata[page as keyof typeof seoMetadata]) {
      const pageMeta = seoMetadata[page as keyof typeof seoMetadata];
      metaTitle = metaTitle || pageMeta.title;
      metaDescription = metaDescription || pageMeta.description;
      metaKeywords = metaKeywords || pageMeta.keywords;
    } else {
      // Default to home
      metaTitle = metaTitle || seoMetadata.home.title;
      metaDescription = metaDescription || seoMetadata.home.description;
      metaKeywords = metaKeywords || seoMetadata.home.keywords;
    }
  }

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": metaTitle,
    "description": metaDescription,
    "url": metaCanonical || "https://carloanpaymentcalculator.com.au"
  };

  const pageDisplayName = calculator 
    ? `${calculator.replace(/([A-Z])/g, ' $1').trim()} Calculator`
    : page === 'home' 
      ? "Home" 
      : String(page).charAt(0).toUpperCase() + String(page).slice(1);

  const breadcrumbData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://carloanpaymentcalculator.com.au"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": pageDisplayName,
        "item": metaCanonical || "https://carloanpaymentcalculator.com.au"
      }
    ]
  };

  return (
    <Helmet>
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="keywords" content={metaKeywords} />
      <link rel="canonical" href={metaCanonical} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={metaCanonical} />
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content="https://carloanpaymentcalculator.com.au/og-image.jpg" />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={metaCanonical} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content="https://carloanpaymentcalculator.com.au/twitter-image.jpg" />
      
      {/* Australia-specific */}
      <meta name="geo.region" content="AU" />
      <meta name="geo.position" content="-25.2744;133.7751" /> {/* Australia's approx. center */}
      <meta name="ICBM" content="-25.2744, 133.7751" />
      
      {/* Additional metadata */}
      <meta name="author" content="Car Loan Payment Calculator" />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="revisit-after" content="7 days" />
      
      {/* Structured data */}
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;